import React from 'react';

import { Control, Controller } from 'react-hook-form';

import { Grid, MenuItem, Stack, TextField } from '@mui/material';

import NativeTourCard from './NativeTourCard';
import NativeTourOfferPicker from './NativeTourOfferPicker';
import NativeTourRelatedList from './NativeTourRelatedList';
import { NativeTourForm, RELATED_YEARS } from './nativeTourForm';

interface Props {
  control: Control<NativeTourForm>;
  tourOfferId: string;
}

type YearLinkField = 'relationships.nextYear' | 'relationships.previousYear';

const YEAR_LINKS: ReadonlyArray<{ name: YearLinkField; title: string; description: string }> = [
  {
    name: 'relationships.nextYear',
    title: 'Next year tour',
    description: "This tour's next season, and the year it's for.",
  },
  {
    name: 'relationships.previousYear',
    title: 'Previous year tour',
    description: "This tour's last season, and the year it was for.",
  },
];

/** How this tour links to others: by season and by likeness. Shared reviews are under User reviews. */
export default function NativeTourRelatedSection({ control, tourOfferId }: Props) {
  return (
    <Stack spacing={3}>
      <Grid container spacing={3}>
        {YEAR_LINKS.map(({ name, title, description }) => (
          <Grid key={name} size={{ xs: 12, lg: 6 }}>
            <NativeTourCard title={title} description={description}>
              <Controller
                control={control}
                name={name}
                render={({ field }) => (
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 8 }}>
                      <NativeTourOfferPicker
                        label="Tour"
                        value={field.value?.tour ?? null}
                        excludeIds={[tourOfferId]}
                        onChange={(tour) => field.onChange(tour ? { tour, year: field.value?.year ?? null } : null)}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                      <TextField
                        select
                        label="Year"
                        value={field.value?.year ?? ''}
                        onChange={(event) =>
                          field.value && field.onChange({ ...field.value, year: Number(event.target.value) })
                        }
                        disabled={!field.value}
                        required={!!field.value}
                        error={!!field.value && field.value.year === null}
                        helperText={!!field.value && field.value.year === null ? 'Pick the year' : undefined}
                        fullWidth
                      >
                        {/* A year saved before this range started is still shown. */}
                        {[...new Set([...(field.value?.year ? [field.value.year] : []), ...RELATED_YEARS])].map(
                          (year) => (
                            <MenuItem key={year} value={year}>
                              {year}
                            </MenuItem>
                          ),
                        )}
                      </TextField>
                    </Grid>
                  </Grid>
                )}
              />
            </NativeTourCard>
          </Grid>
        ))}
      </Grid>

      <NativeTourCard title="Similar tours" description="Suggested on the tour page, in this order.">
        <Controller
          control={control}
          name="relationships.similar"
          render={({ field }) => (
            <NativeTourRelatedList
              value={field.value}
              onChange={field.onChange}
              selfId={tourOfferId}
              addLabel="Add a similar tour"
              emptyText="No similar tours."
            />
          )}
        />
      </NativeTourCard>
    </Stack>
  );
}
